import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { ClientAcquisitionFunnel, ClientValueData } from '../../../types/dashboard';

interface ClientGrowthChartProps {
  acquisitionData?: ClientAcquisitionFunnel[];
  valueData?: ClientValueData[];
  data?: ClientAcquisitionFunnel[] | ClientValueData[];
  type: 'acquisition' | 'value' | 'growth';
  height?: number;
  className?: string;
}

const ClientGrowthChart: React.FC<ClientGrowthChartProps> = ({ 
  acquisitionData = [],
  valueData = [],
  data,
  type,
  height = 300,
  className = ''
}) => {
  // const { strings } = useLocalization(); // Will be used when we add more localized strings
  // Use data prop if provided, otherwise fall back to specific data props
  const chartData = data || (type === 'acquisition' ? acquisitionData : valueData);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-GB').format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
            {label}
          </p>
          <div className="space-y-1">
            {payload.filter((entry: any) => entry && entry.value !== undefined).map((entry: any, index: number) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  {entry.dataKey === 'count' ? 'Count:' : 
                   entry.dataKey === 'conversionRate' ? 'Conversion Rate:' :
                   entry.dataKey === 'totalValue' ? 'Total Value:' : 'Value:'}
                </span>
                <span className="text-sm font-semibold text-slate-800 dark:text-white">
                  {entry.dataKey === 'conversionRate' ? `${entry.value}%` :
                   entry.dataKey === 'totalValue' ? formatCurrency(entry.value) :
                   formatNumber(entry.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (type === 'acquisition' && chartData.length > 0) {
    return (
      <div className={`w-full ${className}`}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="stage" 
              stroke="#64748B"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="#64748B"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="count" 
              fill="#1A2B4C"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'value' && chartData.length > 0) {
    return (
      <div className={`w-full ${className}`}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="valueRange" 
              stroke="#64748B"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="#64748B"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="count" 
              fill="#C9A14B"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'growth') {
    // Use actual data from props or show empty state
    const growthData = data || [];

    return (
      <div className={`w-full ${className}`}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={growthData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="month" 
              stroke="#64748B"
              fontSize={12}
            />
            <YAxis 
              stroke="#64748B"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="clients"
              stroke="#3B82F6"
              strokeWidth={3}
              dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className={`w-full ${className} flex items-center justify-center`}>
      <div className="text-center text-slate-500 dark:text-slate-400">
        <p>No data available</p>
      </div>
    </div>
  );
};

export default ClientGrowthChart;
